import { ReactNode } from 'react';
import NextLink from 'next/link';
import classnames from 'classnames';

import { SalesBreakpoint } from '@/components/Breakpoint';

import LogoSVG from '@/svg/logo.svg';
import GitHubSVG from '@/svg/icons/github.svg';
import LinkedInSVG from '@/svg/icons/linkedin.svg';
import TwitterSVG from '@/svg/icons/twitter.svg';
import SlackSVG from '@/svg/icons/slack.svg';
import DevDotToSVG from '@/svg/icons/dev-dot-to.svg';
import YouTubeSVG from '@/svg/icons/youtube.svg';
import GraphQLSVG from '@/svg/icons/graphql.svg';

type LinkProps = {
  children: ReactNode;
  href: string;
};

function Link({ children, href }: LinkProps) {
  return (
    <NextLink href={href} prefetch={false}>
      <a className="text-zodiac font-medium hover:text-cornflower text-sm">
        {children}
      </a>
    </NextLink>
  );
}

export default function Footer({
  stickyForm = false,
  removeSalesBreakpoint = false,
}) {
  const year = new Date().getFullYear();

  const footerClass = classnames(`bg-whisper`, {
    'pt-56 md:pt-64': stickyForm,
  });

  return (
    <>
      {!removeSalesBreakpoint && (
        <div className="bg-haiti">
          <div className="max-w-6xl mx-auto px-6">
            <div className="-mx-6">
              <SalesBreakpoint />
            </div>
          </div>
        </div>
      )}

      <footer className={footerClass}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="py-6 md:py-12 lg:py-20 border-b border-primary-100">
            <div className="md:flex -mx-6">
              <div className="md:w-1/4 px-6">
                <ul className="mt-6 leading-loose">
                  <li className="text-light-haiti text-sm uppercase tracking-wider font-medium mb-3">
                    Company
                  </li>
                  <li>
                    <Link href="/about-us">About Us</Link>
                  </li>
                  <li>
                    <Link href="/contact">Contact Sales</Link>
                  </li>
                  <li>
                    <Link href="/careers">Careers</Link>
                  </li>
                  <li>
                    <Link href="/support">Support</Link>
                  </li>
                  <li>
                    <Link href="/community">Community</Link>
                  </li>
                  <li>
                    <Link href="/faq">FAQ</Link>
                  </li>
                  <li>
                    <Link href="/terms">Terms</Link>
                  </li>
                  <li>
                    <Link href="/privacy">Privacy</Link>
                  </li>
                  <li>
                    <Link href="/imprint">Imprint</Link>
                  </li>
                </ul>
              </div>

              <div className="md:w-1/4 px-6">
                <ul className="mt-6 leading-loose">
                  <li className="text-light-haiti text-sm uppercase tracking-wider font-medium mb-3">
                    Key Concepts &amp; Features
                  </li>
                  <li>
                    <Link href="/graphql-headless-cms">GraphQL CMS</Link>
                  </li>
                  <li>
                    <Link href="/graphql">Why A GraphQL CMS?</Link>
                  </li>
                  <li>
                    <Link href="/content-federation">Content Federation</Link>
                  </li>
                  <li>
                    <Link href="/composable-architectures">
                      Composable Architectures
                    </Link>
                  </li>
                  <li>
                    <Link href="/react-cms">React CMS</Link>
                  </li>
                  <li>
                    <Link href="/nextjs-cms">Next.js CMS</Link>
                  </li>
                  <li>
                    <Link href="/gatsby-cms">Gatsby CMS</Link>
                  </li>
                  <li>
                    <Link href="/vue-cms">Vue CMS</Link>
                  </li>
                  <li>
                    <Link href="/static-sites">Static Sites</Link>
                  </li>
                  <li>
                    <Link href="/webhooks">Webhooks</Link>
                  </li>
                  <li>
                    <Link href="/content-localization">
                      Content Localization
                    </Link>
                  </li>
                  <li>
                    <Link href="/mutation-api">Mutation API</Link>
                  </li>
                  <li>
                    <Link href="/query-api">Query API</Link>
                  </li>
                </ul>
              </div>

              <div className="md:w-1/4 px-6">
                <ul className="mt-6 leading-loose">
                  <li className="text-light-haiti text-sm uppercase tracking-wider font-medium mb-3">
                    Product
                  </li>
                  <li>
                    <Link href="/features">Features</Link>
                  </li>
                  <li>
                    <Link href="/for-engineering">
                      GraphCMS For Engineering Teams
                    </Link>
                  </li>
                  <li>
                    <Link href="/for-content-creators">
                      GraphCMS For Content Creators
                    </Link>
                  </li>
                  <li>
                    <Link href="/for-businesses">GraphCMS For Businesses</Link>
                  </li>
                  <li>
                    <Link href="/enterprise-headless-cms">For Enterprise</Link>
                  </li>
                  <li>
                    <Link href="/partners">Partners</Link>
                  </li>
                  <li>
                    <Link href="/security-and-compliance">Security</Link>
                  </li>
                  <li>
                    <Link href="/pricing">Pricing</Link>
                  </li>
                  <li>
                    <Link href="/changelog">Changelog</Link>
                  </li>
                  <li>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://status.graphcms.com"
                      className="text-zodiac font-medium hover:text-cornflower text-sm"
                    >
                      API Status
                    </a>
                  </li>
                </ul>
              </div>

              <div className="md:w-1/4 px-6">
                <ul className="mt-6 leading-loose">
                  <li className="text-light-haiti text-sm uppercase tracking-wider font-medium mb-3">
                    Resources
                  </li>
                  <li>
                    <Link href="/academy/headless-cms">
                      What Is A Headless CMS?
                    </Link>
                  </li>
                  <li>
                    <Link href="/academy/what-is-graphql">
                      What Is GraphQL?
                    </Link>
                  </li>
                  <li>
                    <Link href="/case-studies">Customer Case Studies</Link>
                  </li>
                  <li>
                    <Link href="/academy">Academy</Link>
                  </li>
                  <li>
                    <Link href="/docs">Documentation</Link>
                  </li>
                  <li>
                    <Link href="/resources">Resource Hub</Link>
                  </li>
                  <li>
                    <Link href="/events">Events</Link>
                  </li>
                  <li>
                    <Link href="/blog">Blog</Link>
                  </li>
                  <li>
                    <Link href="/use-cases">Solutions</Link>
                  </li>
                  <li>
                    <Link href="/customers">Customers</Link>
                  </li>
                  <li>
                    <Link href="/write-for-graphcms">Write for GraphCMS</Link>
                  </li>
                  <li>
                    <Link href="/newsroom">GraphCMS Newsroom</Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="py-6 md:py-12">
            <div className="md:flex md:items-center">
              <div className="flex md:w-2/5 items-center mb-6 md:mb-0">
                <a
                  href="https://github.com/graphcms"
                  rel="nofollow noopener noreferrer"
                  className="text-light-haiti hover:text-cornflower"
                >
                  <span className="sr-only">GitHub</span>
                  <GitHubSVG className="w-5 h-5" />
                </a>

                <a
                  href="https://www.linkedin.com/company/graphcms"
                  rel="nofollow noopener noreferrer"
                  className="ml-6 text-light-haiti hover:text-cornflower"
                >
                  <span className="sr-only">LinkedIn</span>
                  <LinkedInSVG className="w-5 h-5" />
                </a>

                <a
                  href="https://twitter.com/graphcms"
                  rel="nofollow noopener noreferrer"
                  className="ml-6 text-light-haiti hover:text-cornflower"
                >
                  <span className="sr-only">Twitter</span>
                  <TwitterSVG className="w-5 h-5" />
                </a>

                <a
                  href="https://slack.graphcms.com"
                  rel="nofollow noopener noreferrer"
                  className="ml-6 text-light-haiti hover:text-cornflower"
                >
                  <span className="sr-only">Slack</span>
                  <SlackSVG className="w-5 h-5" />
                </a>

                <a
                  href="https://dev.to/graphcms"
                  rel="nofollow noopener noreferrer"
                  className="ml-6 text-light-haiti hover:text-cornflower"
                >
                  <span className="sr-only">Dev.to</span>
                  <DevDotToSVG className="w-5 h-5" />
                </a>

                <a
                  href="https://www.youtube.com/c/GraphCMS"
                  rel="nofollow noopener noreferrer"
                  className="ml-6 text-light-haiti hover:text-cornflower"
                >
                  <span className="sr-only">YouTube</span>
                  <YouTubeSVG className="w-5 h-5" />
                </a>

                <a
                  href="https://graphqlconf.org"
                  rel="nofollow noopener noreferrer"
                  className="ml-6 text-light-haiti hover:text-cornflower"
                >
                  <span className="sr-only">GraphQL Conf.</span>
                  <GraphQLSVG className="w-5 h-5" />
                </a>
              </div>

              <div className="md:w-1/5 hidden md:flex md:items-center md:justify-center">
                <LogoSVG className="text-haiti h-8" />
              </div>

              <div className="md:w-2/5 text-light-haiti text-sm md:text-right">
                &copy; {year}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
